import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import LanguageSelector from '../components/LanguageSelector';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Alert,
    Chip,
    CircularProgress,
    IconButton
} from '@mui/material'; // Removed Tooltip, Snackbar imports
import ShareIcon from '@mui/icons-material/Share';
import { format } from 'date-fns';
import '../styles/pages/LandingPage.css';

interface QuestionPaper {
    paper_id: number;
    title: string;
    description: string;
    theme_id: number;
    theme_title: string;
    activation_date: string;
    expiry_date: string;
    created_at: string;
    questions_count: number;
    attempted: boolean;
    is_active: boolean;
}

const LandingPage: React.FC = () => {
    const [copiedPaperId, setCopiedPaperId] = useState<number | null>(null);
    const [showCopiedAlert, setShowCopiedAlert] = useState(false);
    const handleShare = (paper: QuestionPaper) => {
        const url = `${window.location.origin}/#quizzes`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedPaperId(paper.paper_id);
            setShowCopiedAlert(true);
            setTimeout(() => setShowCopiedAlert(false), 3000);
        });
    };
    const handleAlertClose = () => {
        setShowCopiedAlert(false);
    };

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [papers, setPapers] = useState<QuestionPaper[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getAvailabilityText = (paper: QuestionPaper) => {
        const now = new Date();
        const activationDate = new Date(paper.activation_date);
        const expiryDate = new Date(paper.expiry_date);

        if (activationDate > now) {
            return t('quiz.availabilityStatus.comingSoon', { date: format(activationDate, 'dd-MMM-yyyy') });
        } else {
            return t('quiz.availabilityStatus.available');
        }
    };

    const isQuizAvailable = (paper: QuestionPaper) => {
        const now = new Date();
        const activationDate = new Date(paper.activation_date);
        return activationDate <= now;
    };

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard');
        }

        const fetchPapers = async () => {
            try {
                const response = await api.get('/quiz/public/papers');
                const sortedPapers = response.data.sort((a: QuestionPaper, b: QuestionPaper) => 
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setPapers(sortedPapers);
            } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to load quizzes');
            } finally {
                setLoading(false);
            }
        };

        fetchPapers();
    }, [isAuthenticated, navigate]);

    // Robust scroll to 'Available Quizzes' section if hash is #quizzes (works in SPA/React)
    useEffect(() => {
        function scrollToQuizzes(retries = 10) {
            if (window.location.hash === '#quizzes') {
                const quizzesSection = document.getElementById('quizzes');
                if (quizzesSection) {
                    quizzesSection.scrollIntoView({ behavior: 'smooth' });
                } else if (retries > 0) {
                    setTimeout(() => scrollToQuizzes(retries - 1), 100);
                }
            }
        }
        scrollToQuizzes();
        const onHashChange = () => scrollToQuizzes();
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, [loading]);

    const handleStartQuiz = async (paperId: number) => {
        if (!isAuthenticated) {
            navigate('/login', { 
                state: { 
                    message: t('auth.pleaseLogin'),
                    returnTo: '/',
                    quizId: paperId
                } 
            });
            return;
        }

        try {
            const response = await api.post('/quiz/attempts', { 
                paper_id: paperId
            });
            
            if (response.data && response.data.attempt_id) {
                navigate(`/quiz/${response.data.attempt_id}`);
            }
        } catch (error: any) {
            setError(error.response?.data?.error || t('quiz.startFailed'));
        }
    };

    return (
        <div className="landing-page">
            <header className="header">
                <nav className="nav-container">
                    <div className="logo">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src="/SDG.png" alt="SDG Logo" style={{ width: '40px', marginRight: '10px' }} />
                            <span>SDG</span>
                        </Box>
                    </div>
                    <div className="nav-links">
                        <a href="#features">{t('common.features')}</a>
                        <a href="/quizzes">{t('common.quizzes')}</a>
                        <a href="#about">{t('common.about')}</a>
                        <a href="#contact">{t('common.contact')}</a>
                        <a href="https://fundhives.com" target="_blank" rel="noopener noreferrer" className="fundhives-nav-link">
                            <img src="/images/fundhives.png" alt="FundHives" className="fundhives-nav-logo" />
                        </a>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LanguageSelector isDark={false} />
                            <a href="/login" className="btn-login">{t('common.login')}</a>
                            <a href="/register" className="btn-register">{t('common.register')}</a>
                        </Box>
                    </div>
                </nav>
            </header>

            <main>
                <section id="hero" className="hero" style={{ 
                    backgroundImage: `linear-gradient(
                        rgba(0, 0, 0, 0.25), 
                        rgba(0, 0, 0, 0.25)
                    ), url(${process.env.PUBLIC_URL}/images/hero-bg.jpg)` 
                }}>
                    <div className="hero-content">
                        <h1>{t('landing.hero.title')}</h1>
                        <p>{t('landing.hero.subtitle')}</p>
                        
                        {!loading && papers.length > 0 && (() => {
    // Use the fixed current time as per user instruction
    const now = new Date('2025-05-03T19:29:48+08:00');
    const filteredPapers = papers.filter(paper => {
        const isActive = paper.is_active;
        const activationDate = new Date(paper.activation_date);
        const expiryDate = new Date(paper.expiry_date);
        return isActive && activationDate <= now && now <= expiryDate;
    }).slice(0, 3);
    return filteredPapers.length > 0 && (
        <div className="hero-latest-quizzes">
            <h3>{t('landing.hero.latestQuizzes')}</h3>
            <div className="latest-quizzes-grid">
                {filteredPapers.map((paper) => (
                    <div key={paper.paper_id} className="latest-quiz-tile">
                        <Typography variant="h6" className="quiz-title">
                            {paper.title}
                        </Typography>
                        <Chip 
                            label={paper.theme_title}
                            color="secondary"
                            size="small"
                            className="theme-chip"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleStartQuiz(paper.paper_id)}
                            className="start-quiz-btn"
                            disabled={!isQuizAvailable(paper)}
                        >
                            {isQuizAvailable(paper) ? t('quiz.startQuizButton') : t('quiz.comingSoon')}
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
})()}

                        <div className="hero-buttons">
                            <a href="/register" className="btn-primary">{t('common.register')}</a>
                            <a 
                                href="/quizzes" 
                                className="btn-secondary"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/quizzes');
                                }}
                            >
                                {t('landing.hero.viewDetails')}
                            </a>
                        </div>

                        <div className="fundhives-section">
                            <p>{t('landing.hero.mangrove.support')}</p>
                            <a href="https://fundhives.com" target="_blank" rel="noopener noreferrer" className="fundhives-link">
                                <img src="/images/fundhives.png" alt="FundHives Logo" className="fundhives-logo" />
                                FundHives
                            </a>
                            <p className="fundhives-description">{t('landing.hero.mangrove.description')}</p>
                        </div>
                    </div>
                </section>

                <section id="quizzes" className="quizzes">
                    <Container maxWidth="lg">
                        <Typography variant="h2" className="section-title">{t('quiz.availableQuizzes')}</Typography>
                        {showCopiedAlert && (
                            <Alert severity="success" sx={{ mb: 3 }} onClose={handleAlertClose}>
                                {t('quiz.copiedLink')}
                            </Alert>
                        )}
                        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                        {loading ? (
                            <Box display="flex" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {papers.slice(0, 9).map((paper) => (
                                        <Grid item xs={12} sm={6} md={4} key={paper.paper_id}>
                                            <Card className="quiz-card">
                                                <CardContent>
                                                    <Typography variant="h6" component="h3">
                                                        {paper.title}
                                                    </Typography>
                                                    <Chip 
                                                        label={paper.theme_title}
                                                        color="secondary"
                                                        size="small"
                                                        className="chip"
                                                    />
                                                    <Typography variant="body2" className="description">
                                                        {paper.description}
                                                    </Typography>
                                                    <Box>
                                                        <Typography variant="body2" className="expiry-date">
                                                            {getAvailabilityText(paper)}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                                <CardActions>
    {isQuizAvailable(paper) ? (
        <Button
            size="small"
            variant="contained"
            color="primary"
            className="start-quiz-btn"
            onClick={() => handleStartQuiz(paper.paper_id)}
        >
            {t('quiz.startQuizButton')}
        </Button>
    ) : (
        <Button
            size="small"
            variant="outlined"
            disabled
            sx={{
                borderColor: '#9e9e9e',
                color: '#9e9e9e',
                '&.Mui-disabled': {
                    borderColor: '#9e9e9e',
                    color: '#9e9e9e'
                }
            }}
        >
            {t('quiz.comingSoon')}
        </Button>
    )}
    <IconButton onClick={() => handleShare(paper)} aria-label="share">
        <ShareIcon sx={{ color: '#fff' }} />
    </IconButton>
</CardActions>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                                {papers.length > 9 && (
                                    <Box display="flex" justifyContent="center" mt={4}>
                                        <a 
                                            href="/quizzes" 
                                            className="btn-secondary"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate('/quizzes');
                                            }}
                                        >
                                            {t('quiz.moreQuizzes')}
                                        </a>
                                    </Box>
                                )}
                            </>
                        )}
                    </Container>
                </section>

                <section id="features" className="features">
                    <div className="features-content">
                        <h2>{t('landing.features.title')}</h2>
                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="fas fa-graduation-cap"></i>
                                </div>
                                <h3>{t('landing.features.interactive')}</h3>
                                <p>{t('landing.features.interactiveDesc')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="fas fa-laptop"></i>
                                </div>
                                <h3>{t('landing.features.personalized')}</h3>
                                <p>{t('landing.features.personalizedDesc')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="fas fa-certificate"></i>
                                </div>
                                <h3>{t('landing.features.certificates')}</h3>
                                <p>{t('landing.features.certificatesDesc')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="about" className="about">
                    <div className="about-content">
                        <h2>{t('landing.about.title')}</h2>
                        <p>{t('landing.about.description')}</p>
                        <div className="stats">
                            <div className="stat-item">
                                <h3>1000+</h3>
                                <p>{t('landing.about.students')}</p>
                            </div>
                            <div className="stat-item">
                                <h3>50+</h3>
                                <p>{t('landing.about.courses')}</p>
                            </div>
                            <div className="stat-item">
                                <h3>95%</h3>
                                <p>{t('landing.about.successRate')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="projects" className="projects">
                    <div className="projects-content">
                        <h2>{t('landing.projects.title')}</h2>
                        <div className="project-grid">
                            <div className="project-card">
                                <div className="project-images">
                                    <img src={`${process.env.PUBLIC_URL}/projects/sdg-camp/1.jpg`} alt="SDG Camp Activity 1" />
                                    <img src={`${process.env.PUBLIC_URL}/projects/sdg-camp/2.jpg`} alt="SDG Camp Activity 2" />
                                    <img src={`${process.env.PUBLIC_URL}/projects/sdg-camp/3.jpg`} alt="SDG Camp Activity 3" />
                                </div>
                                <div className="project-info">
                                    <h3>{t('landing.projects.sdgCamp')}</h3>
                                    <p>{t('landing.projects.sdgCampDesc')}</p>
                                </div>
                            </div>
                            <div className="project-card">
                                <div className="project-images">
                                    <img src={`${process.env.PUBLIC_URL}/projects/forest-school/1.jpg`} alt="Forest School Activity 1" />
                                    <img src={`${process.env.PUBLIC_URL}/projects/forest-school/2.jpg`} alt="Forest School Activity 2" />
                                    <img src={`${process.env.PUBLIC_URL}/projects/forest-school/3.jpg`} alt="Forest School Activity 3" />
                                </div>
                                <div className="project-info">
                                    <h3>{t('landing.projects.forestSchool')}</h3>
                                    <p>{t('landing.projects.forestSchoolDesc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>


                <section id="contact" className="contact">
                    <div className="contact-content">
                        <h2>{t('landing.contact.title')}</h2>
                        <div className="contact-info">
                            <div className="contact-details">
                                <p>
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>{t('landing.contact.address')}</span>
                                </p>
                                <p>
                                    <i className="fas fa-envelope"></i>
                                    <span>{t('landing.contact.email')}</span>
                                </p>
                                <p>
                                    <i className="fas fa-globe"></i>
                                    <span>{t('landing.contact.website')}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-section">
                        <h3>SDG</h3>
                        <p>{t('landing.footer.description')}</p>
                    </div>
                    <div className="footer-section">
                        <h3>{t('landing.footer.quickLinks')}</h3>
                        <a href="#features">{t('common.features')}</a>
                        <a href="/quizzes">{t('common.quizzes')}</a>
                        <a href="#about">{t('common.about')}</a>
                        <a href="#contact">{t('common.contact')}</a>
                    </div>
                    <div className="footer-section">
                        <h3>{t('landing.contactUs')}</h3>
                        <p>{t('landing.footer.email')}: admin@sdg.asia</p>
                        <p>{t('landing.footer.phone')}: +1 234 567 890</p>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>&copy; {new Date().getFullYear()} IM Digital SDN. BHD. {t('landing.footer.copyright')}</p>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
